import { useCallback, useEffect, useState } from 'react';

import {
  DRIP_CAMPAIGN_TYPES,
  type DripCampaignTypeSlug,
  type DripMessageTemplateRow,
  type DripTemplateAudience,
  type DripTemplateSummary,
  type DripWorkflowNode,
  type LeadSource,
} from '@bcc/shared';

import { useAuth } from '../../../contexts/AuthContext';
import { SITE_DRIP_INNER, SITE_DRIP_SURFACE, SITE_FORM_CONTROL } from '../../../constants/siteUi';
import { DripTemplateWorkflowEditor } from './DripTemplateWorkflowEditor';
import {
  createDripTemplateApi,
  deleteDripTemplateApi,
  fetchDripMessageTemplates,
  fetchDripTemplatesList,
  fetchDripTemplate,
  updateDripTemplateApi,
} from '../dripAuthoringApi';

const SOURCE_LABEL: Record<LeadSource, string> = {
  ira_401k: 'IRA / 401(k)',
  contact: 'Contact',
  generic: 'Generic',
  event_rsvp: 'Event RSVP',
  book_401k: 'Book (401k)',
};

const AUDIENCE_OPTIONS: { id: DripTemplateAudience; label: string }[] = [
  { id: 'lead', label: 'Lead' },
  { id: 'everyone', label: 'Everyone' },
  { id: 'source', label: 'Source' },
  { id: 'tag', label: 'Tag' },
];

function savedAudienceLine(t: DripTemplateSummary): string {
  const aud = t.audience ?? 'everyone';
  switch (aud) {
    case 'lead':
      return 'Lead';
    case 'everyone':
      return 'Everyone';
    case 'source':
      return `Source · ${SOURCE_LABEL[t.audienceSource ?? 'contact']}`;
    case 'tag':
      return t.audienceTag ? `Tag · ${t.audienceTag}` : 'Tag';
    default:
      return 'Everyone';
  }
}

/** @deprecated Browser-only fallback — migrated to API when possible */
export const DRIP_CUSTOM_TEMPLATES_STORAGE_KEY = 'bcc-drip-custom-templates-v1';

export type DripCustomTemplate = {
  id: string;
  name: string;
  templateNotes: string;
  campaignType: string;
  workflow: DripWorkflowNode[];
  updatedAt: string;
};

function mapLegacyCampaignType(raw: string): DripCampaignTypeSlug {
  switch (raw) {
    case 'lead_nurture':
    case 'reminders':
    case 'sales_follow_ups':
    case 'onboarding':
    case 're_engagement':
    case 'event_campaigns':
      return raw;
    case 'nurture_sequence':
      return 'lead_nurture';
    case 'event_reminder':
      return 'event_campaigns';
    case 'follow_up':
      return 'sales_follow_ups';
    case 'nurture':
      return 'lead_nurture';
    case 'event_followup':
      return 'event_campaigns';
    case 'reengagement':
      return 're_engagement';
    case 'newsletter':
      return 'lead_nurture';
    default:
      return 'lead_nurture';
  }
}

function initialTriggerNode(): DripWorkflowNode {
  return {
    id: crypto.randomUUID(),
    kind: 'trigger',
    title: 'Trigger',
    subtitle: 'Describe how contacts enter this sequence',
  };
}

function normalizeStoredTemplate(r: Record<string, unknown>): DripCustomTemplate | null {
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return null;

  const workflowRaw = r.workflow;
  let workflow: DripWorkflowNode[] = [];
  if (Array.isArray(workflowRaw)) {
    workflow = workflowRaw
      .filter(
        (n): n is DripWorkflowNode =>
          !!n &&
          typeof n === 'object' &&
          typeof (n as DripWorkflowNode).id === 'string' &&
          typeof (n as DripWorkflowNode).kind === 'string' &&
          typeof (n as DripWorkflowNode).title === 'string',
      )
      .map((n) => ({
        ...n,
        subtitle: typeof n.subtitle === 'string' ? n.subtitle : undefined,
        triggerKey: typeof n.triggerKey === 'string' ? n.triggerKey : undefined,
        email: n.email && typeof n.email === 'object' ? (n.email as DripWorkflowNode['email']) : undefined,
        sms: n.sms && typeof n.sms === 'object' ? (n.sms as DripWorkflowNode['sms']) : undefined,
      }));
  }

  const legacyDesc =
    typeof r.description === 'string'
      ? r.description
      : typeof r.templateNotes === 'string'
        ? r.templateNotes
        : '';

  if (workflow.length === 0) {
    workflow = [
      {
        id: crypto.randomUUID(),
        kind: 'trigger',
        title: 'Trigger',
        subtitle: legacyDesc.slice(0, 500) || 'Entry condition',
      },
    ];
  }

  return {
    id: r.id,
    name: r.name,
    templateNotes: typeof r.templateNotes === 'string' ? r.templateNotes : legacyDesc,
    campaignType: typeof r.campaignType === 'string' ? r.campaignType : DRIP_CAMPAIGN_TYPES[0].value,
    workflow,
    updatedAt: typeof r.updatedAt === 'string' ? r.updatedAt : new Date().toISOString(),
  };
}

export function loadDripCustomTemplatesFromStorage(): DripCustomTemplate[] {
  try {
    const raw = localStorage.getItem(DRIP_CUSTOM_TEMPLATES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((row) => normalizeStoredTemplate(row as Record<string, unknown>))
      .filter((x): x is DripCustomTemplate => x !== null);
  } catch {
    return [];
  }
}

/** Build drip templates — persisted to Postgres via staff API. */
export function DripTemplatesPanel() {
  const { session } = useAuth();
  const token = session?.access_token ?? null;

  const [saved, setSaved] = useState<DripTemplateSummary[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [emailLib, setEmailLib] = useState<DripMessageTemplateRow[]>([]);
  const [smsLib, setSmsLib] = useState<DripMessageTemplateRow[]>([]);

  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftCampaignType, setDraftCampaignType] = useState<DripCampaignTypeSlug>(DRIP_CAMPAIGN_TYPES[0].value);
  const [draftNotes, setDraftNotes] = useState('');
  const [draftAudience, setDraftAudience] = useState<DripTemplateAudience>('everyone');
  const [draftAudienceSource, setDraftAudienceSource] = useState<LeadSource>('contact');
  const [draftAudienceTag, setDraftAudienceTag] = useState('');
  const [draftNodes, setDraftNodes] = useState<DripWorkflowNode[]>([initialTriggerNode()]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [saveBusy, setSaveBusy] = useState(false);

  const reloadMessageLibraries = useCallback(async () => {
    if (!token) return;
    try {
      const [em, sm] = await Promise.all([
        fetchDripMessageTemplates(token, 'email'),
        fetchDripMessageTemplates(token, 'sms'),
      ]);
      setEmailLib(em);
      setSmsLib(sm);
    } catch {
      /* non-fatal */
    }
  }, [token]);

  const reloadTemplates = useCallback(async () => {
    if (!token) {
      setSaved([]);
      return;
    }
    setLoadError(null);
    try {
      const list = await fetchDripTemplatesList(token);
      setSaved(list);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load templates.');
      setSaved([]);
    }
  }, [token]);

  const migrateLocalStorageIfEmpty = useCallback(async () => {
    if (!token) return;
    try {
      const remote = await fetchDripTemplatesList(token);
      if (remote.length > 0) return;
      const local = loadDripCustomTemplatesFromStorage();
      if (local.length === 0) return;
      for (const t of local) {
        await createDripTemplateApi(
          {
            name: t.name,
            campaignType: mapLegacyCampaignType(t.campaignType),
            templateNotes: t.templateNotes,
            workflow: t.workflow.map(({ id: _id, ...rest }) => rest),
            audience: 'everyone',
            audienceSource: null,
            audienceTag: null,
          },
          token,
        );
      }
      localStorage.removeItem(DRIP_CUSTOM_TEMPLATES_STORAGE_KEY);
    } catch {
      /* keep localStorage if migration fails */
    }
  }, [token]);

  const resetNewDraft = useCallback(() => {
    const first = initialTriggerNode();
    setDraftId(null);
    setDraftName('');
    setDraftCampaignType(DRIP_CAMPAIGN_TYPES[0].value);
    setDraftNotes('');
    setDraftAudience('everyone');
    setDraftAudienceSource('contact');
    setDraftAudienceTag('');
    setDraftNodes([first]);
    setSelectedStepId(first.id);
  }, []);

  useEffect(() => {
    void (async () => {
      await migrateLocalStorageIfEmpty();
      await reloadTemplates();
      await reloadMessageLibraries();
    })();
  }, [migrateLocalStorageIfEmpty, reloadMessageLibraries, reloadTemplates]);

  useEffect(() => {
    resetNewDraft();
  }, [resetNewDraft]);

  useEffect(() => {
    if (draftNodes.length && !selectedStepId) {
      setSelectedStepId(draftNodes[0].id);
    }
    if (selectedStepId && !draftNodes.some((n) => n.id === selectedStepId)) {
      setSelectedStepId(draftNodes[0]?.id ?? null);
    }
  }, [draftNodes, selectedStepId]);

  const loadDraft = useCallback(
    async (summary: DripTemplateSummary) => {
      if (!token) return;
      try {
        const detail = await fetchDripTemplate(summary.id, token);
        setDraftId(detail.id);
        setDraftName(detail.name);
        setDraftCampaignType(detail.campaignType);
        setDraftNotes(detail.templateNotes);
        setDraftAudience(detail.audience ?? 'everyone');
        setDraftAudienceSource(detail.audienceSource ?? 'contact');
        setDraftAudienceTag(detail.audienceTag ?? '');
        setDraftNodes(detail.workflow.length ? detail.workflow : [initialTriggerNode()]);
        setSelectedStepId(detail.workflow[0]?.id ?? null);
      } catch {
        setLoadError('Could not load template detail.');
      }
    },
    [token],
  );

  async function saveTemplate() {
    if (!token || !draftName.trim()) return;
    setSaveBusy(true);
    setLoadError(null);
    try {
      const body = {
        name: draftName.trim(),
        campaignType: draftCampaignType,
        templateNotes: draftNotes.trim(),
        workflow: draftNodes.map(({ id: _id, ...rest }) => rest),
        audience: draftAudience,
        audienceSource: draftAudience === 'source' ? draftAudienceSource : null,
        audienceTag: draftAudience === 'tag' ? draftAudienceTag.trim() : null,
      };
      if (draftId) {
        await updateDripTemplateApi(draftId, body, token);
        const detail = await fetchDripTemplate(draftId, token);
        setDraftNodes(detail.workflow);
        setSelectedStepId(detail.workflow[0]?.id ?? null);
      } else {
        const created = await createDripTemplateApi(body, token);
        setDraftId(created.id);
        setDraftNodes(created.workflow);
        setSelectedStepId(created.workflow[0]?.id ?? null);
      }
      await reloadTemplates();
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaveBusy(false);
    }
  }

  async function deleteSaved(id: string) {
    if (!token) return;
    try {
      await deleteDripTemplateApi(id, token);
      await reloadTemplates();
      if (draftId === id) resetNewDraft();
    } catch {
      setLoadError('Delete failed.');
    }
  }

  const typeLabel = (v: string) => DRIP_CAMPAIGN_TYPES.find((t) => t.value === v)?.label ?? v;

  return (
    <div className="space-y-6">
      <section className={`${SITE_DRIP_SURFACE} p-5 md:p-6`}>
        <h2 className="font-display text-base font-bold uppercase tracking-[0.08em] text-[var(--home-text-on-dark)]">
          Drip templates
        </h2>
        <p className="mt-2 max-w-2xl font-body text-xs leading-relaxed text-[var(--home-muted-on-dark)]">
          Name your campaign template, choose a campaign type, and build the sequence. Drag step types (email, wait, task, webhook,
          …) from the palette into the workflow. Email and SMS steps support plain text or HTML (email), plus reusable message
          templates stored in the database.
        </p>
      </section>

      {!token ? (
        <p className="text-sm text-[var(--home-muted-on-dark)]">Sign in as staff to create and save templates.</p>
      ) : null}

      {loadError ? (
        <p className="rounded-lg border border-amber-400/30 bg-amber-950/20 px-3 py-2 text-sm text-amber-100/90">{loadError}</p>
      ) : null}

      <section className={`${SITE_DRIP_SURFACE} p-5 md:p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
            {draftId ? `Edit template` : 'New template'}
          </h3>
          <button
            type="button"
            className="rounded-lg border border-white/12 bg-white/[0.08] px-3 py-1.5 font-display text-[0.58rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] hover:bg-white/[0.12]"
            onClick={resetNewDraft}
          >
            Clear &amp; new
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label
              htmlFor="drip-tpl-campaign-name"
              className="block font-display text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]"
            >
              Template name
            </label>
            <input
              id="drip-tpl-campaign-name"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              className={`mt-1.5 w-full max-w-xl ${SITE_FORM_CONTROL}`}
              placeholder="e.g. Accredited nurture — post webinar"
              maxLength={200}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="drip-tpl-type"
                className="block font-display text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]"
              >
                Campaign type
              </label>
              <select
                id="drip-tpl-type"
                value={draftCampaignType}
                onChange={(e) => setDraftCampaignType(e.target.value as DripCampaignTypeSlug)}
                className={`mt-1.5 w-full ${SITE_FORM_CONTROL}`}
              >
                {DRIP_CAMPAIGN_TYPES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="drip-tpl-notes"
                className="block font-display text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]"
              >
                Template notes (optional)
              </label>
              <input
                id="drip-tpl-notes"
                value={draftNotes}
                onChange={(e) => setDraftNotes(e.target.value)}
                className={`mt-1.5 w-full ${SITE_FORM_CONTROL}`}
                placeholder="Internal notes — not sent to recipients"
                maxLength={500}
              />
            </div>
          </div>
        </div>

        <div className={`mt-6 ${SITE_DRIP_INNER} p-4 md:p-5`}>
          <p className="font-display text-[0.55rem] font-bold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
            Audience
          </p>
          <p className="mt-1 text-[11px] leading-relaxed text-[var(--home-muted-on-dark)]">
            Choose who this template applies to. Tag audience pairs with lead tags on the Leads page so you can target segments.
          </p>
          <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label="Template audience">
            {AUDIENCE_OPTIONS.map((opt) => {
              const on = draftAudience === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDraftAudience(opt.id)}
                  className={`rounded-xl border px-3 py-2 font-display text-[0.58rem] font-bold uppercase tracking-[0.1em] transition ${
                    on
                      ? 'border-[var(--home-miami-blue)]/45 bg-[var(--home-miami-blue)]/15 text-[var(--home-text-on-dark)] ring-1 ring-[var(--home-miami-blue)]/25'
                      : 'border-white/[0.12] bg-white/[0.06] text-[var(--home-muted-on-dark)] hover:border-white/20 hover:text-[var(--home-text-on-dark)]'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {draftAudience === 'source' ? (
            <div className="mt-4">
              <label
                htmlFor="drip-audience-source"
                className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
              >
                Lead source
              </label>
              <select
                id="drip-audience-source"
                value={draftAudienceSource}
                onChange={(e) => setDraftAudienceSource(e.target.value as LeadSource)}
                className={`mt-1.5 max-w-md ${SITE_FORM_CONTROL}`}
              >
                {(Object.keys(SOURCE_LABEL) as LeadSource[]).map((k) => (
                  <option key={k} value={k}>
                    {SOURCE_LABEL[k]}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {draftAudience === 'tag' ? (
            <div className="mt-4">
              <label
                htmlFor="drip-audience-tag"
                className="block font-display text-[0.55rem] font-semibold uppercase tracking-[0.12em] text-[var(--home-muted-on-dark)]"
              >
                Tag name
              </label>
              <input
                id="drip-audience-tag"
                value={draftAudienceTag}
                onChange={(e) => setDraftAudienceTag(e.target.value)}
                className={`mt-1.5 max-w-md ${SITE_FORM_CONTROL}`}
                placeholder="Matches lowercase tags on leads (e.g. accredited)"
                maxLength={80}
              />
            </div>
          ) : null}
        </div>

        <div className="mt-8">
          <h4 className="font-display text-sm font-bold uppercase tracking-[0.08em] text-[var(--home-text-on-dark)]">
            Automation workflow
          </h4>
          <p className="mt-1 max-w-4xl text-xs text-[var(--home-muted-on-dark)]">
            Three columns: step chips on the left (drag into the list), your sequence in the middle, and step details on the right.
          </p>
          <div className="mt-4">
            <DripTemplateWorkflowEditor
              nodes={draftNodes}
              onNodesChange={setDraftNodes}
              selectedId={selectedStepId}
              onSelect={setSelectedStepId}
              token={token}
              emailLibrary={emailLib}
              smsLibrary={smsLib}
              onMessageTemplateSaved={() => void reloadMessageLibraries()}
            />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!token || !draftName.trim() || draftNodes.length === 0 || saveBusy}
            className="rounded-xl border border-white/[0.14] bg-white/[0.12] px-5 py-2.5 font-display text-[0.62rem] font-bold uppercase tracking-[0.12em] text-[var(--home-text-on-dark)] ring-1 ring-white/10 transition hover:bg-white/[0.16] disabled:cursor-not-allowed disabled:opacity-45"
            onClick={() => void saveTemplate()}
          >
            {saveBusy ? 'Saving…' : 'Save template'}
          </button>
        </div>
      </section>

      <section className={`${SITE_DRIP_SURFACE} p-5 md:p-6`}>
        <h3 className="font-display text-[0.65rem] font-bold uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
          Saved templates ({saved.length})
        </h3>
        {saved.length === 0 ? (
          <p className="mt-4 text-sm text-[var(--home-muted-on-dark)]">No saved templates yet.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {saved.map((t) => (
              <li
                key={t.id}
                className={`${SITE_DRIP_INNER} flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-[var(--home-text-on-dark)]">{t.name}</p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    {typeLabel(t.campaignType)} · {t.stepCount} step{t.stepCount === 1 ? '' : 's'} · {savedAudienceLine(t)}
                  </p>
                  <p className="mt-2 font-mono text-[10px] text-[var(--home-muted-on-dark)]">
                    Updated {new Date(t.updatedAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!token}
                    className="rounded-lg border border-white/14 bg-white/[0.08] px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--home-text-on-dark)] hover:bg-white/[0.12] disabled:opacity-45"
                    onClick={() => void loadDraft(t)}
                  >
                    Load
                  </button>
                  <button
                    type="button"
                    disabled={!token}
                    className="rounded-lg border border-red-400/35 bg-red-950/25 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-red-200/90 hover:bg-red-950/40 disabled:opacity-45"
                    onClick={() => void deleteSaved(t.id)}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
